/*
  Silk — procedural fabric renderer used as photo stand-ins in the Barbara Skko concepts.
  Usage: <div class="silk" data-silk="ivory" data-seed="3" data-angle="20"></div>
  Any element with data-silk gets a background-image rendered once by a single shared WebGL context.
  Palettes: ivory, champagne, blush, noir, oxblood, sea, ochre, sage, pearl, ink, rose, sand
  Optional: data-folds="1.0" (fold density), data-light="0.6" (key light angle), data-anim (slow breathing; canvas instead of image)
*/
(() => {
  const PALETTES = {
    ivory:     [[0.70, 0.66, 0.60], [0.97, 0.95, 0.91], [1.00, 1.00, 0.98]],
    champagne: [[0.55, 0.46, 0.35], [0.90, 0.83, 0.72], [1.00, 0.97, 0.90]],
    blush:     [[0.62, 0.46, 0.45], [0.94, 0.84, 0.81], [1.00, 0.96, 0.95]],
    noir:      [[0.01, 0.01, 0.012], [0.13, 0.13, 0.14], [0.70, 0.70, 0.72]],
    oxblood:   [[0.16, 0.02, 0.04], [0.42, 0.06, 0.10], [0.95, 0.55, 0.55]],
    sea:       [[0.03, 0.14, 0.26], [0.10, 0.36, 0.56], [0.75, 0.90, 1.00]],
    ochre:     [[0.48, 0.30, 0.06], [0.86, 0.62, 0.22], [1.00, 0.92, 0.70]],
    sage:      [[0.30, 0.35, 0.29], [0.68, 0.73, 0.65], [0.96, 0.98, 0.93]],
    pearl:     [[0.60, 0.62, 0.67], [0.90, 0.91, 0.93], [1.00, 1.00, 1.00]],
    ink:       [[0.03, 0.05, 0.12], [0.10, 0.14, 0.30], [0.62, 0.70, 0.95]],
    rose:      [[0.55, 0.20, 0.28], [0.86, 0.50, 0.56], [1.00, 0.88, 0.90]],
    sand:      [[0.50, 0.42, 0.33], [0.84, 0.77, 0.66], [0.99, 0.96, 0.90]],
  };

  const VERT = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';
  const FRAG = `
precision highp float;
uniform vec2 res; uniform float seed, angle, folds, light, time;
uniform vec3 cDark, cMid, cHi;
float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7))+seed*13.7)*43758.5453);}
float noise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
  return mix(mix(hash(i),hash(i+vec2(1,0)),f.x),mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),f.x),f.y);}
float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<3;i++){v+=a*noise(p);p*=2.01;a*=.5;}return v;}
float height(vec2 p){
  float ca=cos(angle),sa=sin(angle);
  p=mat2(ca,-sa,sa,ca)*p;
  vec2 w=vec2(fbm(p*0.32+seed+time*0.02),fbm(p*0.32-seed*1.7-time*0.015));
  p+=1.6*(w-0.5);
  float x=p.x*folds;
  float h=0.;
  h+=sin(x*1.55+2.4*fbm(p*0.22+seed))*0.62;
  h+=sin(x*2.9+p.y*0.35+seed)*0.22;
  h+=sin(x*5.1-p.y*0.6+seed*2.1)*0.08;
  return h;
}
void main(){
  vec2 uv=gl_FragCoord.xy/res.y*2.7;
  float e=0.006;
  float h=height(uv);
  float hx=height(uv+vec2(e,0.))-h, hy=height(uv+vec2(0.,e))-h;
  vec3 n=normalize(vec3(-hx/e*0.62,-hy/e*0.62,1.));
  vec3 L=normalize(vec3(cos(light),sin(light),0.9));
  float diff=clamp((dot(n,L)+0.25)/1.25,0.,1.);
  vec3 H=normalize(L+vec3(0,0,1));
  float nh=clamp(dot(n,H),0.,1.);
  float spec=pow(nh,14.)*0.32+pow(nh,70.)*0.35;
  vec3 col=mix(cDark,cMid,smoothstep(0.15,0.95,diff));
  col=mix(col,cHi,clamp(spec,0.,1.));
  float vig=smoothstep(1.4,0.2,length(gl_FragCoord.xy/res-0.5));
  col*=mix(0.92,1.0,vig);
  col+=(hash(gl_FragCoord.xy+time)-0.5)*0.01;
  gl_FragColor=vec4(col,1.);
}`;

  let gl, prog, canvas, uni = {};
  function init() {
    canvas = document.createElement('canvas');
    gl = canvas.getContext('webgl', { preserveDrawingBuffer: true, antialias: false });
    if (!gl) return false;
    const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) console.error("silk shader:", gl.getShaderInfoLog(o)); return o; };
    prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return false;
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    ['res', 'seed', 'angle', 'folds', 'light', 'time', 'cDark', 'cMid', 'cHi'].forEach((u) => { uni[u] = gl.getUniformLocation(prog, u); });
    return true;
  }

  function draw(w, h, o, t = 0) {
    canvas.width = w; canvas.height = h;
    gl.viewport(0, 0, w, h);
    const pal = PALETTES[o.palette] || PALETTES.ivory;
    gl.uniform2f(uni.res, w, h);
    gl.uniform1f(uni.seed, o.seed);
    gl.uniform1f(uni.angle, o.angle * Math.PI / 180);
    gl.uniform1f(uni.folds, o.folds);
    gl.uniform1f(uni.light, o.light);
    gl.uniform1f(uni.time, t);
    gl.uniform3fv(uni.cDark, pal[0]); gl.uniform3fv(uni.cMid, pal[1]); gl.uniform3fv(uni.cHi, pal[2]);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  function opts(el) {
    return {
      palette: el.dataset.silk || 'ivory',
      seed: parseFloat(el.dataset.seed || '1'),
      angle: parseFloat(el.dataset.angle || '12'),
      folds: parseFloat(el.dataset.folds || '1'),
      light: parseFloat(el.dataset.light || '0.9'),
    };
  }

  function fallback(el) {
    const pal = PALETTES[el.dataset.silk] || PALETTES.ivory;
    const c = (a) => `rgb(${a.map((v) => Math.round(v * 255)).join(',')})`;
    el.style.backgroundImage = `linear-gradient(${el.dataset.angle || 12}deg, ${c(pal[0])}, ${c(pal[1])} 40%, ${c(pal[2])} 55%, ${c(pal[1])} 70%, ${c(pal[0])})`;
  }

  function renderAll(root = document) {
    const els = Array.from(root.querySelectorAll('[data-silk]'));
    if (!els.length) return;
    if (!gl && !init()) { els.forEach(fallback); return; }
    const scale = Math.min(window.devicePixelRatio || 1, 1.5);
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      const w = Math.max(64, Math.min(1600, Math.round((r.width || 400) * scale)));
      const h = Math.max(64, Math.min(1600, Math.round((r.height || 600) * scale)));
      draw(w, h, opts(el));
      el.style.backgroundImage = `url(${canvas.toDataURL('image/jpeg', 0.86)})`;
      el.style.backgroundSize = 'cover';
      el.style.backgroundPosition = 'center';
    });
  }

  window.Silk = { renderAll, PALETTES };
  const go = () => renderAll();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go); else go();
  let t; window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(go, 300); });
})();
